import { useState, type ReactNode } from "react";
import { StatCard } from "@/components/ui/StatCard";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { LoadingState, ErrorState } from "@/components/ui/AsyncState";
import { TimeSeriesChart } from "@/components/ui/TimeSeriesChart";
import { RangePicker, rangeQuery, type RangeSelection } from "@/components/ui/RangePicker";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents, formatPercent } from "@/lib/format";
import type { AnalyticsSummary, AnalyticsPoint } from "@platform/shared";

function shortLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium uppercase tracking-wide text-ink-soft">{title}</h2>
      {children}
    </section>
  );
}

function ChartCard({
  title,
  points,
  pick,
  color,
  money,
  className = "",
}: {
  title: string;
  points: AnalyticsPoint[];
  pick: (p: AnalyticsPoint) => number;
  color: string;
  money?: boolean;
  className?: string;
}) {
  return (
    <Card className={`p-5 ${className}`}>
      <p className="mb-3 font-medium text-ink">{title}</p>
      <TimeSeriesChart
        points={points.map((p) => ({ label: shortLabel(p.date), value: money ? pick(p) / 100 : pick(p) }))}
        color={color}
        formatValue={money ? (v) => formatCurrencyFromCents(Math.round(v * 100)) : undefined}
      />
    </Card>
  );
}

export function Analytics() {
  const [selection, setSelection] = useState<RangeSelection>({ range: "30d" });
  const query = rangeQuery(selection);
  const summary = useApi<{ summary: AnalyticsSummary }>(`/api/analytics/summary?${query}`, [query]);
  const timeseries = useApi<{ points: AnalyticsPoint[] }>(`/api/analytics/timeseries?${query}`, [query]);

  const s = summary.data?.summary;
  const points = timeseries.data?.points ?? [];
  const hasActivity = points.some(
    (p) => p.totalCalls + p.smsSent + p.smsReceived + p.leadsCreated + p.qualifiedLeads + p.convertedLeads > 0
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-ink">Analytics</h1>
          <p className="mt-1 text-sm text-ink-soft">
            How much value Callback is generating for your business.
            {s && (
              <>
                {" "}
                <span className="whitespace-nowrap">
                  {shortLabel(s.startDate)} – {shortLabel(s.endDate)} (UTC)
                </span>
              </>
            )}
          </p>
        </div>
        <RangePicker value={selection} onChange={setSelection} />
      </div>

      {summary.isLoading && <LoadingState label="Loading analytics…" />}
      {summary.error && <ErrorState message={summary.error} onRetry={summary.refetch} />}

      {s && (
        <>
          <Section title="Revenue">
            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="border-success-500/30 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ink-soft">Confirmed revenue</p>
                  <Badge tone="success">Confirmed</Badge>
                </div>
                <p className="mt-2 font-display text-3xl text-ink">{formatCurrencyFromCents(s.confirmedRevenueCents)}</p>
                <p className="mt-1 text-xs text-ink-soft">
                  Actual revenue you recorded on won leads ({s.convertedLeads} won).
                </p>
              </Card>
              <Card className="border-dashed p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ink-soft">Estimated lead value</p>
                  <Badge>Estimate</Badge>
                </div>
                <p className="mt-2 font-display text-3xl text-ink-soft">{formatCurrencyFromCents(s.estimatedLeadValueCents)}</p>
                <p className="mt-1 text-xs text-ink-soft">
                  A projection from the estimated value of leads that became qualified. Not money received.
                </p>
              </Card>
            </div>
          </Section>

          <Section title="Calls">
            <div className="grid gap-4 sm:grid-cols-4">
              <StatCard label="Total calls" value={String(s.totalCalls)} />
              <StatCard label="Answered" value={String(s.answeredCalls)} />
              <StatCard label="Missed" value={String(s.missedCalls)} />
              <StatCard
                label="Missed calls followed up"
                value={String(s.missedCallsFollowedUp)}
                hint={`${formatPercent(s.responseRate)} response rate`}
              />
            </div>
          </Section>

          <Section title="Conversations">
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="SMS sent" value={String(s.smsSent)} />
              <StatCard label="SMS received" value={String(s.smsReceived)} />
              <StatCard label="AI conversations" value={String(s.aiConversations)} />
            </div>
          </Section>

          <Section title="Leads">
            <div className="grid gap-4 sm:grid-cols-4">
              <StatCard label="Leads created" value={String(s.leadsCreated)} />
              <StatCard
                label="Qualified"
                value={String(s.qualifiedLeads)}
                hint={`${formatPercent(s.qualificationRate)} qualification rate`}
              />
              <StatCard label="Won" value={String(s.convertedLeads)} />
              <StatCard label="Response rate" value={formatPercent(s.responseRate)} hint="Missed calls that got a follow-up" />
            </div>
            <p className="text-xs text-ink-soft">
              Rates show “—” when there was nothing to measure in the period. Counts are by the day each event
              happened, so a lead created earlier can be qualified or won inside this range.
            </p>
          </Section>
        </>
      )}

      {timeseries.error && <ErrorState message={timeseries.error} onRetry={timeseries.refetch} />}

      {timeseries.data && !hasActivity && (
        <Card className="flex h-40 items-center justify-center px-5 text-center text-sm text-ink-soft">
          No activity recorded in this date range.
        </Card>
      )}

      {hasActivity && (
        <Section title="Trends">
          <div className="grid gap-4 sm:grid-cols-2">
            <ChartCard title="Calls" points={points} pick={(p) => p.totalCalls} color="#3D5F87" />
            <ChartCard title="Missed calls" points={points} pick={(p) => p.missedCalls} color="#CB5F2C" />
            <ChartCard title="SMS sent" points={points} pick={(p) => p.smsSent} color="#3D5F87" />
            <ChartCard title="AI conversations" points={points} pick={(p) => p.aiConversations} color="#3D5F87" />
            <ChartCard title="Qualified leads" points={points} pick={(p) => p.qualifiedLeads} color="#227A5C" />
            <ChartCard title="Won leads" points={points} pick={(p) => p.convertedLeads} color="#227A5C" />
            <ChartCard
              title="Confirmed revenue"
              points={points}
              pick={(p) => p.confirmedRevenueCents}
              color="#227A5C"
              money
            />
            <ChartCard
              title="Estimated lead value (not revenue)"
              points={points}
              pick={(p) => p.estimatedLeadValueCents}
              color="#9AA5B1"
              money
            />
          </div>
        </Section>
      )}
    </div>
  );
}
